import { ChevronLeft, Clock, LogOut, Palette, Pin, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import { LanguageSwitcher } from '@/components/px/LanguageSwitcher'
import { ModuleAttribution } from '@/components/px/ModuleAttribution'
import { NotificationBell } from '@/components/px/NotificationBell'
import { navText } from '@/components/px/navText'
import {
  levelTwoSections,
  moduleOwnsPath,
  navGroups,
  pinnedMeDestinations,
  railLevelFor,
  sectionsBesideSwitcher,
  type NavDestination,
  type NavGroupDestinations,
  type NavSection,
} from '@/components/px/nav'
import { Separator } from '@/components/ui/separator'
import { landingRouteFor, navForRole, navGroupsForRole } from '@/access/roleAccess'
import { roleDefinitionFor } from '@/access/roles'
import { modulesForPath } from '@/modules/architecture'
import {
  selectActiveRoleKey,
  selectSessionUser,
  signOut,
  type SessionUser,
} from '@/features/auth/slices/sessionSlice'
import { selectSidebarState, setSidebarState } from '@/features/shell/shellSlice'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { resetAll, useAppDispatch, useAppSelector } from '@/store'

/** Below this width the rail is collapsed regardless of the stored preference. */
const COLLAPSE_QUERY = '(max-width: 64rem)'

/**
 * Local review aid: `VITE_PX_UNLOCK_CONCEPT_NAV=true` in
 * `client/.env.development.local` renders routed coming-soon rows as real links
 * (clock marker kept) so the concept pages can be browsed from the rail. The
 * rail STATE is untouched — `navStateForPath` still says coming-soon, and the
 * Home overview still reports it. Dev server only: `import.meta.env.DEV` is
 * false in every build, and vitest's `test` mode never loads that env file.
 */
const UNLOCK_CONCEPT_NAV =
  import.meta.env.DEV && import.meta.env.VITE_PX_UNLOCK_CONCEPT_NAV === 'true'

/** What the user chip renders. */
interface ShellIdentity {
  initial: string
  name: string
  meta: string
}

/**
 * The user chip's identity, from the REAL session (PX-W6.6, spec §6.3 item 5).
 *
 * Falls back through name → email → the role label so the chip is never blank
 * on a user the backend has not named yet; `meta` states the derived role and
 * the active organisation, which is the pair a viewer needs to know whose
 * permissions they are seeing.
 *
 * ⚠ `roleLabel` is INJECTED, already localised (`common:roles.*`). It used to
 * be read straight off `roleDefinitionFor(role).label`, whose client-side rows
 * still carried the demo client's name — so every real client-side session saw
 * a fictional company in its own identity chip (merged review, C1). The ORG half
 * has always come from the real session and still does.
 */
function shellIdentity(user: SessionUser | null, roleLabel: string): ShellIdentity {
  const { organisations = [] } = user ?? {}
  const name = user?.name?.trim() ?? ''
  const display = name !== '' ? name : (user?.email ?? roleLabel)
  const org = organisations[0]?.orgName ?? organisations[0]?.orgId ?? null
  return {
    initial: display.charAt(0).toUpperCase(),
    name: display,
    meta: org === null ? roleLabel : `${roleLabel} · ${org}`,
  }
}

/**
 * LOCKED LITERAL SHELL — round-1 mandatory fix #3. Every surface renders
 * inside this component; no surface re-authors the top bar, the rail, the
 * context pill or the user chip (round 1's shell drifted in every direction
 * within three screens — this component is the enforcement).
 *
 * Contract:
 *  - Top bar: PX mark → home · module label · breadcrumb · CONTEXT PILL
 *    (aubergine — decided furniture) · user chip (persona of the current role).
 *  - Left rail (optional): small-caps section heads at AA ink; item counts at
 *    AA ink (the control's 2.62:1 counts were the round's cheapest defect);
 *    active item = mint (the locked "active nav" meaning of mint). Pinnable —
 *    see `NavRail` for the pin/hover model. Two levels, one at a time
 *    (CR-RAIL-DRILLDOWN-2026-09-26, spec R.6.4, I-12 … I-18): level 1 is the
 *    module switcher, level 2 the active module's own rail under an
 *    `‹ All modules` back row with the pinned Me rows at the bottom. The
 *    route decides the level — no page passes it.
 *  - One content measure: every surface is centred in the same `max-w-[80rem]`
 *    column, so no screen re-decides its own width.
 *  - The concept reset and SIGN OUT are SHELL affordances, not product claims.
 *    Sign-out is REAL (`POST /auth/logout`); the reset clears the concept act
 *    log and never ends the session.
 *  - TASK-9F6C8EBE added three CENTRAL extensions rather than 32 per-page ones:
 *    (a) one identity resolution for the user chip, total over all ten roles;
 *    (b) a `<ModuleAttribution>` row derived from the pathname, so every
 *    existing surface declares its Rev A modules (AC-6.4); (c) the rail is
 *    filtered through the single central `navForRole` call, so no role ever
 *    sees an item it cannot reach (AC-1.5 / I-1). Header markup, context pill,
 *    NavRail internals, the pin model and the max-w-[80rem] measure are
 *    untouched, and no token was added or changed.
 *  - PX-W6.6 repointed the chip and the rail filter onto the REAL session
 *    (`selectActiveRoleKey`), and retired the demo role switcher and its
 *    borrowed-lens disclosure. The shell imports nothing from `@/demo` (I-2).
 *  - NO window-frame dots, slide eyebrows or any other deck furniture.
 */
export function AppShell({
  moduleLabel,
  breadcrumb,
  contextPill,
  nav,
  children,
}: {
  moduleLabel: string
  breadcrumb: React.ReactNode
  contextPill: string
  nav?: readonly NavSection[]
  children: React.ReactNode
}) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'nav'])
  const location = useLocation()
  const { pathname } = location
  const currentRole = useAppSelector(selectActiveRoleKey)
  const sessionUser = useAppSelector(selectSessionUser)
  const persona = shellIdentity(sessionUser, t(roleDefinitionFor(currentRole).labelKey))
  // At `/` the shell is rendering the role's landing, so the module
  // attribution answers for that landing rather than for the entry point —
  // and so does the rail: this is also its `railPath` (spec R.6.3), so `/`
  // takes the level of the landing it renders.
  const attributionPath = pathname === '/' ? landingRouteFor(currentRole) : pathname
  // TASK-33343000 (AC-1) + CR-RAIL-DRILLDOWN-2026-09-26 (spec I-12/I-13): the
  // level-1 module switcher is BUILT here for every authenticated page —
  // `navGroups(null)` is the whole product's declared model; `null` because
  // the shell has no project prop — and `NavRail` renders it at level 1 only.
  // On a module's route the rail drills down to that module's own rows
  // (level 2; a concrete engagement's rail still comes from the page's `nav`
  // prop), and the back row brings the switcher back in place.
  const switcherGroups = navGroupsForRole(currentRole, navGroups(null))

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-4 border-b border-border bg-card px-5">
        <Link
          to="/"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-serif text-sm font-bold text-primary-foreground"
          aria-label={t('shell.home', { ns: 'nav' })}
        >
          PX
        </Link>
        <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {moduleLabel}
        </div>
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{breadcrumb}</div>
        <div className="rounded-full bg-primary px-3 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-primary-foreground">
          {contextPill}
        </div>
        <div className="flex items-center gap-2 border-l border-border pl-4">
          {/*
           * PX-W6.2 (AC-6): the notification bell — the ONE additive header
           * furniture this ticket mounts, before the user chip. Header markup,
           * rail, guards and persona furniture are untouched.
           */}
          <NotificationBell />
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-full bg-people-bg text-sm font-semibold text-people-ink"
          >
            {persona.initial}
          </span>
          <div className="hidden leading-tight lg:block">
            <div className="text-sm font-medium">{persona.name}</div>
            <div className="text-xs text-muted-foreground">{persona.meta}</div>
          </div>
          {/*
            * PX-W6.6 retired the shell role switcher and the borrowed-lens
            * disclosure that stood beside it. They existed to compare six DEMO
            * identities without a sign-out cycle; with a real session the
            * switcher would be a false affordance — it would change what the
            * rail shows while changing nothing the server enforces. The role
            * shown in the chip's meta line is the one `canAccess` gates on.
            */}
          {/*
            * TASK-FB5DA982 (C-6) — the language switcher takes the retired
            * role-switcher's slot: after the identity chip, before Designs.
            */}
          <LanguageSwitcher />
          <Link
            to="/designs"
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            title={t('shell.designsTitle', { ns: 'nav' })}
          >
            <Palette className="h-3 w-3" aria-hidden />
            {t('shell.designs', { ns: 'nav' })}
          </Link>
          <button
            type="button"
            onClick={() => dispatch(resetAll())}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            title={t('shell.resetDemoTitle', { ns: 'nav' })}
          >
            <RotateCcw className="h-3 w-3" aria-hidden />
            {t('shell.resetDemo', { ns: 'nav' })}
          </button>
          <button
            type="button"
            onClick={() => {
              // The REAL sign-out (D-1): POST /auth/logout, wipe the in-memory
              // token, reset the session slice — without it the httpOnly
              // refresh cookie survives and the next protected visit silently
              // re-authenticates. PX-W6.6 dropped the demo-lens reset that used
              // to ride along; there is no lens left to reset.
              void dispatch(signOut())
              navigate('/login')
            }}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            title={t('shell.signOutTitle', { ns: 'nav' })}
          >
            <LogOut className="h-3 w-3" aria-hidden />
            {t('shell.signOut', { ns: 'nav' })}
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <NavRail
          groups={switcherGroups}
          railPath={attributionPath}
          locationKey={location.key}
          nav={nav && navForRole(currentRole, nav)}
        />
        <main className="min-w-0 flex-1 px-6 py-5">
          <div className="mx-auto w-full max-w-[80rem]">
            <ModuleAttribution modules={modulesForPath(attributionPath)} />
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

/**
 * The "show modules" toggle behind the back row (CR-RAIL-DRILLDOWN-2026-09-26,
 * spec I-13): true only between a back-row activation and the next change of
 * history entry. It is ephemeral view state, so it lives in the rail — never
 * in the store, never persisted, never set by anything but the back row.
 *
 * The reset is React's documented "adjusting some state when a prop changes"
 * pattern (react.dev, "You Might Not Need an Effect"): the state remembers the
 * entry key it belongs to, and a render that sees a different key replaces it
 * before anything paints — no effect, so no frame of a stale level 1 on the
 * next page. Every navigation mints a new `location.key` (push, replace — a
 * same-URL link click included — and pop), so any navigation lapses the
 * toggle. Remembering only "the key the toggle was set at" and comparing it
 * would wrongly revive level 1 on a browser Back to that same entry; the
 * AC-10 history test in `AppShell.test.tsx` fails that alternative.
 */
function useShowModules(locationKey: string): readonly [boolean, () => void] {
  const [state, setState] = useState({ key: locationKey, shown: false })
  if (state.key !== locationKey) setState({ key: locationKey, shown: false })
  const showing = state.shown && state.key === locationKey
  return [showing, () => setState({ key: locationKey, shown: true })] as const
}

/**
 * The pinnable rail (parity with xelerate-ai's AppShell).
 *
 * The pin PREFERENCE lives in the store; the rendered width is DERIVED from it
 * plus the viewport, never stored — so a trip below the breakpoint does not
 * rewrite the preference, and no dispatch ever happens on resize. Hover-expand
 * is a pointer affordance gated on the same viewport predicate, and `hovered`
 * is only ever ARMED while a real pointer is present, so a touch tap cannot
 * latch the rail open (touch fires no `mouseleave`).
 *
 * An in-flow spacer holds the layout width while the rail itself is `fixed`,
 * so the hover expansion overlays the content instead of shoving it sideways.
 *
 * TWO LEVELS, ONE AT A TIME (CR-RAIL-DRILLDOWN-2026-09-26, spec R.6.4,
 * I-12 … I-18). The level is DERIVED — `railLevelFor(railPath)`, overridden
 * to 1 only by the back-row toggle — so no page call site carries it:
 *  - level 1: the module switcher, then the page's own sections minus the
 *    VIS-C2-01 duplicate (only on a level-1 ROUTE; level 1 reached by the
 *    back row shows the switcher alone, focus on the module being left);
 *  - level 2: the `‹ All modules` back row, the active module's sections
 *    (`levelTwoSections`), then — below a divider — the pinned Me rows
 *    (`pinnedMeDestinations`), only when at least one survives.
 * `data-rail-level` on the `<nav>` states which level rendered.
 */
function NavRail({
  nav,
  groups,
  railPath,
  locationKey,
}: {
  /** The current page's own module rail, already role-filtered — optional (a level-2 rail then falls back to the module's rows, spec I-17). */
  nav?: readonly NavSection[]
  /** The level-1 model, already role-filtered to the groups/destinations this role can reach. */
  groups: readonly NavGroupDestinations[]
  /** The pathname, or the role's landing at `/` (spec R.6.3) — the one path every rail rule reads. */
  railPath: string
  /** The current history entry's `location.key` — what the back-row toggle belongs to. */
  locationKey: string
}) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['common', 'nav'])
  const sidebarState = useAppSelector(selectSidebarState)
  const isNarrow = useMediaQuery(COLLAPSE_QUERY)
  const [hovered, setHovered] = useState(false)
  const [showingModules, showModules] = useShowModules(locationKey)

  const pinned = sidebarState === 'expanded' && !isNarrow
  const hoverExpanded = hovered && !isNarrow
  const showExpanded = pinned || hoverExpanded

  const route = railLevelFor(railPath)
  const level: 1 | 2 = route.level === 2 && !showingModules ? 2 : 1
  const destinations = groups.flatMap((group) => group.destinations)
  const activeDestination = destinations.find((destination) => moduleOwnsPath(destination.key, railPath))

  /**
   * VIS-C2-01 (review cycle 2, fix cycle 3) — the LEVEL-1 half of the
   * duplicate guard: the switcher already renders one row per reachable
   * module — for a single-destination module (PX, Notifications) that row's
   * route is also the whole of the page's own section once `navForRole`
   * (applied by the caller before this `nav` prop is even passed in) has run.
   * Rendering that section again would repeat the identical label + route a
   * few rows down, so a section whose ONLY surviving item points at the
   * destination the switcher already shows is dropped here — never a section
   * with two or more surviving rows. The level-2 half is the pinned-row
   * filter in `pinnedMeDestinations` (spec I-15).
   *
   * The filter lives in `sectionsBesideSwitcher` (nav.ts) so its guard for an
   * undefined active destination is unit-tested (TASK-505148A8, finding 8).
   */
  const levelOneSections = route.level === 1 && nav ? sectionsBesideSwitcher(nav, activeDestination?.to) : []
  const moduleSections =
    route.level === 2
      ? levelTwoSections(
          nav,
          destinations.find((destination) => destination.key === route.module),
          route.module
        )
      : []
  const pinnedMe = route.level === 2 ? pinnedMeDestinations(groups, route.module, moduleSections) : []

  // Spec R.6.4: the back row leaves the DOM on the render it switches to level
  // 1, so focus moves to the switcher row of the module being left (the first
  // row when the role has none, e.g. on a RoleBlocked page) — never to <body>.
  // Moving DOM focus is the one job an effect has in this rail.
  const focusRef = useRef<HTMLAnchorElement>(null)
  const focusKey = (activeDestination ?? destinations[0])?.key
  useEffect(() => {
    if (showingModules) focusRef.current?.focus()
  }, [showingModules])

  const pinLabel = t(pinned ? 'shell.unpinSidebar' : 'shell.pinSidebar', { ns: 'nav' })

  return (
    <>
      <div
        className={cn(
          'shrink-0 transition-[width] duration-200 ease-in-out',
          pinned ? 'w-56' : 'w-14'
        )}
      />
      <div
        onMouseEnter={() => !pinned && !isNarrow && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="fixed bottom-0 left-0 top-14 z-30"
      >
        {showExpanded && (
          <button
            type="button"
            onClick={() => dispatch(setSidebarState(pinned ? 'collapsed' : 'expanded'))}
            aria-label={pinLabel}
            title={pinLabel}
            className={cn(
              'absolute left-[13rem] top-4 z-50 flex h-7 w-7 items-center justify-center rounded-full border',
              pinned
                ? 'border-border bg-accent text-accent-foreground'
                : // Unpinned: `bg-background`, not the rail's own `bg-card`, so
                  // the button reads as a control on the white rail (finding 5).
                  'border-border bg-background text-muted-foreground hover:text-foreground'
            )}
          >
            <Pin className={cn('h-3.5 w-3.5', pinned && 'fill-current')} aria-hidden />
          </button>
        )}
        <nav
          aria-label={t('shell.railLabel', { ns: 'nav' })}
          data-rail-level={level}
          className={cn(
            // Owner ruling (2026-09-19): the rail matches the cards' white
            // (`bg-card`), not the page's grey `bg-background` — the same
            // token on the expanded AND the collapsed rail, since both share
            // this one className string. Never a hard-coded #fff/bg-white so
            // a future dark theme stays consistent. `flex flex-col` lets the
            // level-2 pinned block sit at the bottom in normal flow (P-5).
            'flex h-full flex-col overflow-y-auto border-r border-border bg-card px-3 py-4 transition-[width] duration-200 ease-in-out',
            showExpanded ? 'w-56' : 'w-14',
            hoverExpanded && !pinned && 'shadow-lg'
          )}
        >
          {level === 1 ? (
            <>
              <SwitcherGroups
                groups={groups}
                railPath={railPath}
                showExpanded={showExpanded}
                focusKey={focusKey}
                focusRef={focusRef}
              />
              <RailSections sections={levelOneSections} showExpanded={showExpanded} />
            </>
          ) : (
            <>
              <BackRow onActivate={showModules} showExpanded={showExpanded} />
              <RailSections sections={moduleSections} showExpanded={showExpanded} />
              {pinnedMe.length > 0 && <PinnedRows destinations={pinnedMe} showExpanded={showExpanded} />}
            </>
          )}
        </nav>
      </div>
    </>
  )
}

/**
 * A small-caps rail heading — the switcher's group headings and every
 * section's heading use this one markup. Rendered in BOTH states at a fixed
 * height so every row below keeps the same vertical position across
 * collapse/expand — collapsed shows a short hairline in place of the text.
 */
function RailHeading({ label, showExpanded }: { label: string; showExpanded: boolean }) {
  return (
    <div
      className={cn(
        'flex h-6 items-center',
        showExpanded
          ? 'px-2 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground'
          : 'justify-center'
      )}
    >
      {showExpanded ? label : <span aria-hidden className="h-px w-4 bg-border" />}
    </div>
  )
}

/**
 * The classes every linked rail row shares — the destination rows below and
 * the section rows in `RailSections` — so both keep one row height, padding
 * and active treatment. Callers pass it through a `NavLink` class CALLBACK,
 * never a string, so the router adds no `active` class of its own.
 */
const railRowClass = (showExpanded: boolean, isActive: boolean): string =>
  cn(
    'flex h-8 items-center rounded-md text-sm',
    showExpanded ? 'justify-between px-2' : 'justify-center px-0',
    isActive ? 'bg-accent font-semibold text-accent-foreground' : 'text-ink-soft hover:bg-muted'
  )

/**
 * One module destination row — the switcher's rows and the level-2 pinned Me
 * rows render through this single markup, so the two cannot drift apart.
 * `isActive` is module ownership of the rail path, not a URL match, which is
 * why the `NavLink` class callback ignores the router's own flag.
 */
function DestinationRow({
  destination,
  isActive,
  showExpanded,
  rowRef,
}: {
  destination: NavDestination
  isActive: boolean
  showExpanded: boolean
  rowRef?: React.Ref<HTMLAnchorElement>
}) {
  const { t } = useTranslation(['common', 'nav'])
  const Icon = destination.icon
  const label = navText(t, destination.labelKey, destination.label)
  return (
    <NavLink
      ref={rowRef}
      to={destination.to}
      title={label}
      className={() => railRowClass(showExpanded, isActive)}
    >
      {showExpanded ? (
        <span className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </span>
      ) : (
        <Icon className="h-4 w-4" aria-hidden />
      )}
      {!showExpanded && <span className="sr-only">{label}</span>}
    </NavLink>
  )
}

/**
 * TASK-33343000 (AC-1) — the level-1 module switcher. Same row height and
 * icon rules as the sections; a small-caps group heading uses the identical
 * classes the section headings use. `focusRef` lands on the `focusKey` row,
 * which `NavRail` focuses after the back row (spec R.6.4).
 */
function SwitcherGroups({
  groups,
  railPath,
  showExpanded,
  focusKey,
  focusRef,
}: {
  groups: readonly NavGroupDestinations[]
  railPath: string
  showExpanded: boolean
  focusKey: NavDestination['key'] | undefined
  focusRef: React.Ref<HTMLAnchorElement>
}) {
  const { t } = useTranslation(['common', 'nav'])
  return (
    <>
      {groups.map((group) => (
        <div key={group.key} className="mb-5">
          <RailHeading label={navText(t, group.headingKey, group.heading)} showExpanded={showExpanded} />
          <ul>
            {group.destinations.map((destination) => (
              <li key={destination.key}>
                <DestinationRow
                  destination={destination}
                  isActive={moduleOwnsPath(destination.key, railPath)}
                  showExpanded={showExpanded}
                  rowRef={destination.key === focusKey ? focusRef : undefined}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

/**
 * The page/module section rows — the ONE row renderer at both levels (spec
 * I-18), so coming-soon stays inert (I-3) and the dev-only unlock review aid
 * keeps working on level 1 and level 2 alike.
 */
function RailSections({ sections, showExpanded }: { sections: readonly NavSection[]; showExpanded: boolean }) {
  // `nav` resolves the model's labels (TASK-505148A8, C-5) through `navText`;
  // `common` still owns the coming-soon state.
  const { t } = useTranslation(['common', 'nav'])
  return (
    <>
      {sections.map((section) => (
        <div key={section.heading} className="mb-5">
          <RailHeading label={navText(t, section.headingKey, section.heading)} showExpanded={showExpanded} />
          <ul>
            {section.items.map((item) => {
              const Icon = item.icon
              // React keys stay on the EN literal (stable identity across
              // locales); only the rendered text goes through `navText`.
              const key = `${item.to ?? 'no-route'}-${item.label}`
              const label = navText(t, item.labelKey, item.label)
              const unlocked = UNLOCK_CONCEPT_NAV && item.to !== undefined
              if (item.state === 'coming-soon' && !unlocked) {
                // TASK-33343000 (D6, I-3): NO `href`, NO handler — the
                // inertness is structural, not an event handler that a
                // future edit could forget. Still Tab-reachable so a
                // screen-reader user can discover the screen exists.
                //
                // VIS-C-03 (review cycle 2): the full-width "Coming soon"
                // TEXT pill used to sit in this trailing slot and ate
                // ~70px of the 208px expanded rail, clipping 4 of 8
                // Resources labels down to a few characters. The pill is
                // now a compact icon-only marker (`aria-hidden`); the full
                // localised state still reaches assistive tech via
                // `title` (now set in BOTH rail states) and the sr-only
                // span below. Every attribute the tests/audit rely on
                // (`role="link"`, `aria-disabled="true"`, `tabIndex={0}`,
                // `data-nav-state="coming-soon"`, no href, inert on
                // click/Enter/Space) is unchanged.
                return (
                  <li key={key}>
                    <span
                      role="link"
                      aria-disabled="true"
                      tabIndex={0}
                      data-nav-state="coming-soon"
                      // Every rail item carries its full label in `title`
                      // in both rail states, so a truncated label stays
                      // readable (fix cycle 1, GRP-82297E9A).
                      title={`${label} — ${t('nav.comingSoon')}`}
                      className={cn(
                        'flex h-8 items-center rounded-md text-sm text-muted-foreground',
                        showExpanded ? 'justify-between px-2' : 'justify-center px-0'
                      )}
                    >
                      {showExpanded ? (
                        <>
                          <span className="flex min-w-0 items-center gap-2">
                            {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
                            <span className="truncate">{label}</span>
                          </span>
                          <span
                            className="ml-2 flex shrink-0 items-center"
                            aria-hidden
                            data-testid="coming-soon-marker"
                          >
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          </span>
                          <span className="sr-only">{t('nav.comingSoon')}</span>
                        </>
                      ) : Icon ? (
                        <Icon className="h-4 w-4" aria-hidden />
                      ) : (
                        <span aria-hidden className="font-semibold">
                          {label.charAt(0)}
                        </span>
                      )}
                      {!showExpanded && (
                        <span className="sr-only">
                          {label} · {t('nav.comingSoon')}
                        </span>
                      )}
                    </span>
                  </li>
                )
              }
              const to = item.to ?? '/'
              return (
                <li key={key}>
                  <NavLink
                    to={to}
                    end={to.split('/').length <= 3}
                    title={label}
                    className={({ isActive }) => railRowClass(showExpanded, isActive)}
                  >
                    {showExpanded ? (
                      <>
                        <span className="flex min-w-0 items-center gap-2">
                          {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
                          <span className="truncate">{label}</span>
                        </span>
                        {item.count !== undefined && (
                          <span className="v-figure text-xs tabular-nums text-muted-foreground">
                            {item.count}
                          </span>
                        )}
                        {item.state === 'coming-soon' && (
                          <Clock className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                        )}
                      </>
                    ) : Icon ? (
                      <Icon className="h-4 w-4" aria-hidden />
                    ) : (
                      <span aria-hidden className="font-semibold">
                        {label.charAt(0)}
                      </span>
                    )}
                    {!showExpanded && <span className="sr-only">{label}</span>}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </>
  )
}

/**
 * The level-2 back row (CR-RAIL-DRILLDOWN-2026-09-26, Decision R-1, spec
 * R.6.4): it shows level 1 IN PLACE and navigates nowhere, so it is a
 * `<button>` — an `<a>` or `role="link"` would announce a navigation that
 * never happens. A native button gives Tab, Enter and Space for free.
 *
 * A plain `<button>`, not the shadcn `Button`: it must carry the rail rows'
 * own classes and the browser's native focus ring (the design contract —
 * `design/rail-drilldown-2026-09-26/DESIGN-NOTES.md`: the mint `ring` token
 * fails WCAG 1.4.11 on the white rail). The design's three additions to the
 * row classes: `mr-3` expanded with `self-stretch` (the hover fill and focus
 * ring end short of the pin button instead of running under it),
 * `cursor-pointer` (Tailwind v4 preflight gives buttons the default cursor),
 * and `focus-visible:outline-offset-1` (matches the anchors' ring offset).
 * `shrink-0` is measured, not decorative: it is the rail's only direct flex
 * item with a fixed height, and on a short window (1280×560, the My projects
 * rail overflowing) the default `flex-shrink: 1` squeezed it from `h-8` to
 * its 1.25rem line box.
 */
function BackRow({ onActivate, showExpanded }: { onActivate: () => void; showExpanded: boolean }) {
  const { t } = useTranslation(['common', 'nav'])
  const title = t('shell.allModulesTitle', { ns: 'nav' })
  return (
    <button
      type="button"
      data-testid="rail-back"
      onClick={onActivate}
      aria-label={title}
      title={title}
      className={cn(
        'mb-3 flex h-8 shrink-0 cursor-pointer items-center self-stretch rounded-md text-sm text-ink-soft hover:bg-muted focus-visible:outline-offset-1',
        showExpanded ? 'mr-3 justify-start gap-2 px-2' : 'justify-center px-0'
      )}
    >
      <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
      {showExpanded && <span className="truncate">{t('shell.allModules', { ns: 'nav' })}</span>}
    </button>
  )
}

/**
 * The level-2 pinned Me rows (spec R.6.2 / R.6.4): a decorative divider, then
 * the rows, bottom-anchored IN FLOW (`mt-auto` in the flex-column rail), not
 * sticky — the design contract measured that level 2 overflows only on short
 * windows, where the rows simply follow the last section. The divider is
 * `pt-3` + 1px + `mb-3` at both widths, so the rows do not jump on collapse.
 * Never active: the active module's row is dropped before it gets here and
 * Notifications renders level 1.
 */
function PinnedRows({
  destinations,
  showExpanded,
}: {
  destinations: readonly NavDestination[]
  showExpanded: boolean
}) {
  return (
    <div className="mt-auto pt-3">
      {showExpanded ? (
        <Separator className="mb-3" />
      ) : (
        <div className="mb-3 flex h-px justify-center">
          <span aria-hidden className="h-px w-4 bg-border" />
        </div>
      )}
      <ul data-testid="rail-pinned">
        {destinations.map((destination) => (
          <li key={destination.key}>
            <DestinationRow destination={destination} isActive={false} showExpanded={showExpanded} />
          </li>
        ))}
      </ul>
    </div>
  )
}
